import { useAuthorizedRequest } from '@/hooks/useRequest';
import { apiRequest } from '@/lib/api';
import type { Client } from '@/lib/Client';
import type { Lesson } from '@/lib/Lesson';
import type { Subject } from '@/lib/Subject';
import { getSubjectPrettyName } from '@/lib/Subject';
import type { Teacher } from '@/lib/Teacher';
import { t } from '@/lib/i18n';
import { Box, Button, CloseButton, Drawer, Heading, HStack, Input, Portal, Text, VStack } from '@chakra-ui/react';
import { useColorModeValue } from '@/components/ui/color-mode';
import { useAuth0 } from '@auth0/auth0-react';
import { useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import type { EventContentArg, EventClickArg } from '@fullcalendar/core';
import ukLocale from '@fullcalendar/core/locales/uk';
// FullCalendar v6 injects styles programmatically — no CSS imports needed

function jsonFetcher(url: string, token: string) {
    return fetch(url, { headers: { Authorization: `Bearer ${token}` } }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
    });
}

interface CalendarLesson extends Lesson {
    clientName?: string;
    teacherName?: string;
    clientId: string;
}

export function LessonCalendar() {
    const calendarRef = useRef<FullCalendar>(null);

    const { data: lessonsData, error, isLoading, isAuthenticated, mutate: mutateLessons } =
        useAuthorizedRequest<{ lessons: Lesson[] }>('/lessons', jsonFetcher);
    const { data: clientsData } =
        useAuthorizedRequest<{ clients: Client[] }>('/clients', jsonFetcher);
    const { data: teachersData } =
        useAuthorizedRequest<{ teachers: Teacher[] }>('/teachers', jsonFetcher);
    const { data: subjectsData } =
        useAuthorizedRequest<{ subjects: Subject[] }>('/subjects', jsonFetcher);

    const clientMap = useMemo(() => {
        const map: Record<string, Client> = {};
        for (const c of clientsData?.clients ?? []) map[c.id] = c;
        return map;
    }, [clientsData]);

    const teacherMap = useMemo(() => {
        const map: Record<string, Teacher> = {};
        for (const t of teachersData?.teachers ?? []) map[t.id] = t;
        return map;
    }, [teachersData]);

    const events = useMemo(() => {
        return (lessonsData?.lessons ?? [])
            .filter(l => !l.deleted)
            .map(l => {
                // Parse date_time_local (format "2025-10-12 12:45:30") explicitly as local time
                const [yr, mo, dy, hr, mi, sc] = l.date_time_local.split(/[-: ]/).map(Number);
                const start = new Date(yr!, mo! - 1, dy!, hr!, mi!, sc!);
                const end = new Date(start.getTime() + l.duration_minutes * 60 * 1000);
                const clientId = l.student_id.split(':')[0] ?? '';
                const client = clientMap[clientId];
                const teacher = teacherMap[l.teacher_id];

                return {
                    id: l.id,
                    title: `${client?.name ?? clientId.slice(0, 8)} — ${teacher?.name ?? l.teacher_id.slice(0, 8)}${l.subject ? ` (${getSubjectPrettyName(l.subject)})` : ''}`,
                    start,
                    end,
                    extendedProps: {
                        lesson: {
                            ...l,
                            clientName: client?.name ?? clientId.slice(0, 8),
                            teacherName: teacher?.name ?? l.teacher_id.slice(0, 8),
                            clientId,
                        } as CalendarLesson,
                    },
                };
            });
    }, [lessonsData, clientMap, teacherMap]);

    const { getAccessTokenSilently } = useAuth0();

    async function handleToggleComplete(l: Lesson) {
        const token = await getAccessTokenSilently();
        const teacherDatetime = `${l.teacher_id}_${l.date_time_local}`;
        const newCompleted = !l.completed;
        await apiRequest('PUT', `/lessons/${l.student_id}/${teacherDatetime}`, token, {
            student_id: l.student_id,
            teacher_id: l.teacher_id,
            date_time_local: l.date_time_local,
            price_uah: l.price_uah,
            duration_minutes: l.duration_minutes,
            subject: l.subject || undefined,
            individual: l.individual,
            online: l.online,
            completed: newCompleted,
            comment: l.comment || undefined,
            deleted: l.deleted,
        });
        await mutateLessons();
    }

    const [selectedLesson, setSelectedLesson] = useState<CalendarLesson | null>(null);
    const [editLessonOpen, setEditLessonOpen] = useState(false);

    const handleEventClick = (clickInfo: EventClickArg) => {
        const lesson = clickInfo.event.extendedProps.lesson as CalendarLesson;
        if (lesson) {
            setSelectedLesson(lesson);
            setEditLessonOpen(true);
        }
    };

    function renderEventContent(arg: EventContentArg) {
        const lesson = arg.event.extendedProps.lesson as CalendarLesson;
        const l = lesson;
        const statusColor = l.completed ? '#38a169' : '#d69e2e';
        return (
            <Box
                fontSize="xs"
                lineHeight="1.3"
                w="full"
                h="full"
                borderLeft="3px solid"
                borderLeftColor={statusColor}
                pl="3px"
            >
                <Text fontWeight="bold" truncate fontSize="11px" letterSpacing="-0.01em">
                    {l.clientName ?? arg.event.title}
                </Text>
                <Text truncate fontSize="10px" opacity={0.9}>
                    {l.teacherName}{l.subject ? ` — ${getSubjectPrettyName(l.subject)}` : ''}
                </Text>
                <Text fontSize="10px" opacity={0.85}>
                    {l.price_uah}₴ · {l.duration_minutes}хв{' '}
                    <Box as="span" cursor="pointer" onClick={(e: any) => { e.stopPropagation(); handleToggleComplete(l); }} title={l.completed ? 'Mark pending' : 'Mark completed'}>
                        {l.completed ? '✓' : '○'}
                    </Box>
                </Text>
            </Box>
        );
    }

    // All hooks MUST be called before any early return (Rules of Hooks)
    const teachers = useMemo(() => teachersData?.teachers ?? [], [teachersData]);
    const subjects = useMemo(() => subjectsData?.subjects ?? [], [subjectsData]);

    const bg = useColorModeValue('#ffffff', '#1a1a2e');
    const fg = useColorModeValue('#1a1a2e', '#ececec');
    const borderColor = useColorModeValue('#e2e8f0', '#2a2a4a');
    const eventBg = useColorModeValue('#3182ce', '#0f3460');
    const todayBg = useColorModeValue('#ebf8ff', '#1a2744');
    const headerBg = useColorModeValue('#f7fafc', '#16213e');
    const hoverBg = useColorModeValue('#edf2f7', '#1e2a4a');
    const cardBg = useColorModeValue('#ffffff', '#1e1e36');
    const shadow = useColorModeValue('0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.06)', '0 1px 3px rgba(0,0,0,0.3), 0 1px 2px rgba(0,0,0,0.2)');
    const moreLinkColor = useColorModeValue('#3182ce', '#7fc1fe');
    const buttonTextColor = useColorModeValue('#3182ce', '#e2e8f0');
    const weekendBg = useColorModeValue('rgba(0,0,0,0.015)', 'rgba(255,255,255,0.02)');

    if (!isAuthenticated) return <div>{t('label.needs_auth')}</div>;
    if (error) return <div>{t('label.error')}: {(error as Error).message}</div>;

    const selectStyle = {
        width: '100%', padding: '4px 8px', borderRadius: '4px',
        border: '1px solid #ccc', fontSize: '14px', background: 'transparent',
    };

    async function handleEditLesson(attrs: Partial<Lesson>) {
        if (!selectedLesson) return;
        const token = await getAccessTokenSilently();
        const teacherDatetime = `${selectedLesson.teacher_id}_${selectedLesson.date_time_local}`;
        await apiRequest('PUT', `/lessons/${selectedLesson.student_id}/${teacherDatetime}`, token, {
            student_id: selectedLesson.student_id,
            teacher_id: attrs.teacher_id ?? selectedLesson.teacher_id,
            date_time_local: attrs.date_time_local ?? selectedLesson.date_time_local,
            price_uah: attrs.price_uah ?? selectedLesson.price_uah,
            duration_minutes: attrs.duration_minutes ?? selectedLesson.duration_minutes,
            subject: attrs.subject !== undefined ? attrs.subject : selectedLesson.subject,
            individual: attrs.individual ?? selectedLesson.individual,
            online: attrs.online ?? selectedLesson.online,
            completed: attrs.completed ?? selectedLesson.completed,
            comment: attrs.comment !== undefined ? attrs.comment : selectedLesson.comment,
            deleted: attrs.deleted ?? selectedLesson.deleted,
        });
        await mutateLessons();
        setEditLessonOpen(false);
        setSelectedLesson(null);
    }

    return (
        <VStack gap={4} align="stretch">
            <HStack justify="space-between">
                <Heading size="md">{t('schedule.title')}</Heading>
            </HStack>
            <Box
                className="calendar-wrapper"
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                bg={cardBg}
                boxShadow={shadow}
                p={4}
            >
                <style>{`
                    .calendar-wrapper {
                        --fc-page-bg-color: transparent;
                        --fc-neutral-bg-color: transparent;
                        --fc-neutral-text-color: ${fg};
                        --fc-border-color: ${borderColor};
                        --fc-button-text-color: ${buttonTextColor};
                        --fc-button-bg-color: transparent;
                        --fc-button-border-color: ${borderColor};
                        --fc-button-hover-bg-color: ${hoverBg};
                        --fc-button-hover-border-color: ${borderColor};
                        --fc-button-active-bg-color: ${eventBg};
                        --fc-button-active-border-color: ${eventBg};
                        --fc-event-bg-color: ${eventBg};
                        --fc-event-border-color: transparent;
                        --fc-event-text-color: #fff;
                        --fc-today-bg-color: ${todayBg};
                        --fc-list-event-hover-bg-color: ${hoverBg};
                        --fc-now-indicator-color: ${eventBg};
                        --fc-event-overlay-color: rgba(0,0,0,0.1);
                    }

                    /* ── Base ── */
                    .calendar-wrapper .fc {
                        color: ${fg};
                        font-size: 0.875rem;
                    }

                    /* ── Toolbar ── */
                    .calendar-wrapper .fc-toolbar {
                        gap: 8px;
                        flex-wrap: wrap;
                    }
                    .calendar-wrapper .fc-toolbar-title {
                        font-size: 1.125rem;
                        font-weight: 700;
                        letter-spacing: -0.01em;
                        text-transform: capitalize;
                    }

                    /* ── Buttons ── */
                    .calendar-wrapper .fc-button {
                        border-radius: 8px;
                        font-weight: 600;
                        font-size: 0.8125rem;
                        padding: 6px 14px;
                        transition: all 0.15s ease;
                        box-shadow: none;
                        border-width: 1px;
                    }
                    .calendar-wrapper .fc-button:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 2px 6px rgba(0,0,0,0.1);
                    }
                    .calendar-wrapper .fc-button-primary:not(:disabled).fc-button-active {
                        color: #fff;
                        box-shadow: 0 2px 6px rgba(0,0,0,0.15);
                    }
                    .calendar-wrapper .fc-button-primary:not(:disabled):active {
                        transform: translateY(0);
                    }
                    .calendar-wrapper .fc-button-primary:disabled {
                        opacity: 0.4;
                        cursor: not-allowed;
                    }
                    .calendar-wrapper .fc-today-button {
                        font-weight: 700;
                    }
                    .calendar-wrapper .fc-today-button:disabled {
                        opacity: 0.3;
                    }

                    /* ── Header cells (day names) ── */
                    .calendar-wrapper .fc-col-header-cell {
                        background-color: ${headerBg};
                        padding: 10px 0;
                        font-weight: 600;
                        font-size: 0.8125rem;
                        text-transform: uppercase;
                        letter-spacing: 0.04em;
                        color: ${fg};
                        border-bottom: 2px solid ${borderColor};
                    }

                    /* ── Day cells ── */
                    .calendar-wrapper .fc-daygrid-day {
                        transition: background-color 0.1s ease;
                    }
                    .calendar-wrapper .fc-daygrid-day:hover {
                        background-color: ${hoverBg};
                    }
                    .calendar-wrapper .fc-daygrid-day-number {
                        font-size: 0.875rem;
                        font-weight: 500;
                        padding: 6px 8px;
                        color: ${fg};
                    }
                    .calendar-wrapper .fc-day-today .fc-daygrid-day-number {
                        background-color: ${eventBg};
                        color: #fff;
                        border-radius: 50%;
                        width: 28px;
                        height: 28px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0;
                        margin: 4px;
                    }
                    .calendar-wrapper .fc-daygrid-more-link {
                        color: ${moreLinkColor};
                        font-weight: 600;
                        font-size: 0.75rem;
                        padding: 1px 4px;
                    }

                    /* ── Events ── */
                    .calendar-wrapper .fc-event {
                        border-radius: 6px;
                        cursor: pointer;
                        border: none !important;
                        box-shadow: 0 1px 3px rgba(0,0,0,0.12);
                        transition: all 0.12s ease;
                        margin: 1px 3px;
                    }
                    .calendar-wrapper .fc-event:hover {
                        transform: translateY(-1px);
                        box-shadow: 0 3px 8px rgba(0,0,0,0.18);
                        filter: brightness(1.1);
                    }
                    .calendar-wrapper .fc-daygrid-event {
                        border-radius: 6px;
                    }

                    /* ── Week / Day view ── */
                    .calendar-wrapper .fc-timegrid-slot {
                        height: 2.2em;
                    }
                    .calendar-wrapper .fc-timegrid-slot-label {
                        font-size: 0.75rem;
                        color: ${fg};
                        opacity: 0.7;
                    }
                    .calendar-wrapper .fc-timegrid-now-indicator-line {
                        border-color: ${eventBg};
                        border-width: 2px;
                    }
                    .calendar-wrapper .fc-timegrid-now-indicator-arrow {
                        border-color: ${eventBg};
                    }
                    .calendar-wrapper .fc .fc-timegrid-col.fc-day-today {
                        background-color: ${todayBg};
                    }

                    /* ── List view ── */
                    .calendar-wrapper .fc-list {
                        border: none !important;
                    }
                    .calendar-wrapper .fc-list-day-cushion {
                        background-color: ${headerBg};
                        padding: 10px 14px;
                        font-weight: 600;
                    }
                    .calendar-wrapper .fc-list-day-text,
                    .calendar-wrapper .fc-list-day-side-text {
                        color: ${fg};
                        font-size: 0.875rem;
                    }
                    .calendar-wrapper .fc-list-event {
                        cursor: pointer;
                        transition: background-color 0.1s ease;
                    }
                    .calendar-wrapper .fc-list-event:hover td {
                        background-color: ${hoverBg};
                    }
                    .calendar-wrapper .fc-list-event-graphic {
                        padding-left: 8px;
                    }
                    .calendar-wrapper .fc-list-event-time {
                        font-weight: 500;
                        color: ${fg};
                        opacity: 0.8;
                    }
                    .calendar-wrapper .fc-list-event-title {
                        font-weight: 500;
                    }

                    /* ── Borders ── */
                    .calendar-wrapper .fc-scrollgrid {
                        border-color: ${borderColor} !important;
                        border-radius: 8px;
                        overflow: hidden;
                    }
                    .calendar-wrapper .fc-col-header,
                    .calendar-wrapper .fc-daygrid-day-frame,
                    .calendar-wrapper .fc-scrollgrid-section > td {
                        border-color: ${borderColor};
                    }

                    /* ── Popover (e.g. "+N more" click) ── */
                    .calendar-wrapper .fc .fc-popover {
                        background-color: ${cardBg};
                        border-color: ${borderColor};
                        color: ${fg};
                        border-radius: 10px;
                        box-shadow: 0 8px 25px rgba(0,0,0,0.15);
                        max-width: min(90vw, 400px);
                        max-height: min(80vh, 500px);
                        overflow-y: auto;
                    }
                    .calendar-wrapper .fc .fc-popover .fc-popover-header {
                        background-color: ${headerBg};
                        padding: 8px 12px;
                        font-weight: 600;
                    }
                    .calendar-wrapper .fc .fc-popover .fc-popover-body {
                        padding: 4px;
                        max-height: 60vh;
                        overflow-y: auto;
                    }

                    /* ── Non-current month days ── */
                    .calendar-wrapper .fc-day-other .fc-daygrid-day-number {
                        opacity: 0.35;
                    }

                    /* ── Weekend styling ── */
                    .calendar-wrapper .fc-day-sat,
                    .calendar-wrapper .fc-day-sun {
                        background-color: ${weekendBg};
                    }
                `}</style>
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={{
                        left: 'prev,next today',
                        center: 'title',
                        right: 'dayGridMonth,timeGridWeek,timeGridDay,listWeek',
                    }}
                    buttonText={{
                        today: t('schedule.today'),
                        month: t('schedule.month'),
                        week: t('schedule.week'),
                        day: t('schedule.day'),
                        list: t('schedule.list'),
                    }}
                    events={events}
                    eventContent={renderEventContent}
                    eventClick={handleEventClick}
                    height="auto"
                    contentHeight="auto"
                    firstDay={1}
                    weekends={true}
                    locales={[ukLocale]}
                    locale="uk"
                    nowIndicator={true}
                    editable={false}
                    selectable={false}
                    dayMaxEvents={3}
                />
            </Box>

            <Drawer.Root open={editLessonOpen} onOpenChange={e => { if (!e.open) { setEditLessonOpen(false); setSelectedLesson(null); } }} placement="end">
                <Portal>
                    <Drawer.Backdrop />
                    <Drawer.Positioner>
                        <Drawer.Content>
                            <Drawer.Header>
                                <Drawer.Title>{t('lessons.edit_title')}</Drawer.Title>
                            </Drawer.Header>
                            <Drawer.Body>
                                {selectedLesson && <LessonDetailDrawerContent
                                    lesson={selectedLesson}
                                    teachers={teachers}
                                    subjects={subjects}
                                    onSave={handleEditLesson}
                                    onClose={() => { setEditLessonOpen(false); setSelectedLesson(null); }}
                                    selectStyle={selectStyle}
                                    clientMap={clientMap}
                                    teacherMap={teacherMap}
                                />}
                            </Drawer.Body>
                            <Drawer.CloseTrigger asChild>
                                <CloseButton size="xs" variant="solid" />
                            </Drawer.CloseTrigger>
                        </Drawer.Content>
                    </Drawer.Positioner>
                </Portal>
            </Drawer.Root>
        </VStack>
    );
}

function LessonDetailDrawerContent({ lesson, teachers, subjects, onSave, selectStyle, clientMap, teacherMap, onClose }: {
    lesson: CalendarLesson;
    teachers: Teacher[];
    subjects: Subject[];
    onSave: (attrs: Partial<Lesson>) => Promise<void>;
    selectStyle: Record<string, string>;
    clientMap: Record<string, Client>;
    teacherMap: Record<string, Teacher>;
    onClose: () => void;
}) {
    const activeTeachers = teachers.filter(x => !x.deleted);
    const activeSubjects = subjects.filter(x => !x.deleted);
    const [teacherId, setTeacherId] = useState(lesson.teacher_id);
    const [dateTime, setDateTime] = useState(lesson.date_time_local.slice(0, 16).replace(' ', 'T'));
    const [priceUah, setPriceUah] = useState(String(lesson.price_uah));
    const [durationMin, setDurationMin] = useState(String(lesson.duration_minutes));
    const [subject, setSubject] = useState(lesson.subject ?? '');
    const [individual, setIndividual] = useState(lesson.individual);
    const [online, setOnline] = useState(lesson.online);
    const [completed, setCompleted] = useState(lesson.completed);
    const [comment, setComment] = useState(lesson.comment ?? '');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const clientId = lesson.student_id.indexOf(':') > 0 ? lesson.student_id.substring(0, lesson.student_id.indexOf(':')) : lesson.student_id;
    const client = clientMap[clientId];

    async function handleSubmit() {
        const price = parseInt(priceUah, 10);
        const dur = parseInt(durationMin, 10);
        if (isNaN(price) || price < 0) { setError('Invalid price'); return; }
        if (isNaN(dur) || dur <= 0) { setError('Invalid duration'); return; }
        setSubmitting(true); setError('');
        try {
            const dtLocal = dateTime.replace('T', ' ') + ':00';
            await onSave({
                teacher_id: teacherId,
                date_time_local: dtLocal,
                price_uah: price,
                duration_minutes: dur,
                subject: subject || undefined,
                individual,
                online,
                completed,
                comment: comment.trim() || undefined,
            });
            onClose();
        } catch (e) { setError(e instanceof Error ? e.message : 'Error'); }
        finally { setSubmitting(false); }
    }

    const clientLink = client?.name ?? clientId.slice(0, 8);

    return (
        <VStack gap={3} align="stretch">
            {error && <Text color="red.500" fontSize="sm">{error}</Text>}

            <Box>
                <Text fontSize="sm" fontWeight="bold" mb="1">{t('label.client')}</Text>
                <Text fontSize="sm">{clientLink}</Text>
            </Box>
            <Box>
                <Text fontSize="sm" fontWeight="bold" mb="1">{t('label.student_id')}</Text>
                <Text fontSize="xs" color="gray.500">{lesson.student_id}</Text>
            </Box>
            <Box>
                <Text fontSize="sm" mb="1">{t('label.teacher')} *</Text>
                <select value={teacherId} onChange={e => setTeacherId(e.target.value)} style={selectStyle}>
                    {activeTeachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
            </Box>
            <Box>
                <Text fontSize="sm" mb="1">{t('label.date')} *</Text>
                <Input size="sm" type="datetime-local" value={dateTime} onChange={e => setDateTime(e.target.value)} />
            </Box>
            <Box>
                <Text fontSize="sm" mb="1">{t('label.price')} (₴)</Text>
                <Input size="sm" type="number" min="0" value={priceUah} onChange={e => setPriceUah(e.target.value)} />
            </Box>
            <Box>
                <Text fontSize="sm" mb="1">{t('label.duration')}</Text>
                <Input size="sm" type="number" min="1" value={durationMin} onChange={e => setDurationMin(e.target.value)} />
            </Box>
            <Box>
                <Text fontSize="sm" mb="1">{t('label.subject')}</Text>
                <select value={subject} onChange={e => setSubject(e.target.value)} style={selectStyle}>
                    <option value="">{t('subject.none')}</option>
                    {activeSubjects.map(s => <option key={s.id} value={s.name}>{getSubjectPrettyName(s.name)}</option>)}
                </select>
            </Box>
            <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                <input type="checkbox" checked={individual} onChange={e => setIndividual(e.target.checked)} /> {t('label.individual')}
            </label>
            <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                <input type="checkbox" checked={online} onChange={e => setOnline(e.target.checked)} /> {t('label.online')}
            </label>
            <label style={{ display: 'flex', gap: '8px', fontSize: '14px' }}>
                <input type="checkbox" checked={completed} onChange={e => setCompleted(e.target.checked)} /> {t('label.completed')}
            </label>
            <Box>
                <Text fontSize="sm" mb="1">{t('label.comment')}</Text>
                <Input size="sm" value={comment} onChange={e => setComment(e.target.value)} />
            </Box>
            <HStack mt={4}>
                <Button size="sm" onClick={handleSubmit} loading={submitting}>{t('action.save')}</Button>
                <Button size="sm" variant="ghost" onClick={onClose}>{t('action.cancel')}</Button>
            </HStack>
        </VStack>
    );
}
